import { Injectable, inject, signal, computed, Injector } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { HOME_PATH } from '../../constants/app-routes';
import { markTenantResolved, resetTenantResolved } from '../graphql/tenant-resolution';

export interface Tenant {
  id: string;
  name: string;
  slug?: string;
  servicesCategory?: string;
}

/** Query param that carries the just-switched tenant across a hard reload. */
const OVERRIDE_PARAM = 'at';

const BOOT_OVERRIDE_TENANT_ID: string | null = (() => {
  try {
    const id = new URL(window.location.href).searchParams.get(OVERRIDE_PARAM);
    return id && id.length ? id : null;
  } catch {
    return null;
  }
})();

@Injectable({ providedIn: 'root' })
export class TenantService {
  private static readonly STORAGE_KEY = 'active_tenant_id';

  private injector = inject(Injector);

  private _tenants = signal<Tenant[]>([]);
  private _activeTenantId = signal<string | null>(null);

  private bootstrap?: Promise<void>;
  private readonly overrideTenantId: string | null = BOOT_OVERRIDE_TENANT_ID;

  readonly tenants = this._tenants.asReadonly();
  readonly activeTenantId = this._activeTenantId.asReadonly();
  readonly activeTenant = computed(() => {
    const id = this._activeTenantId();
    return this._tenants().find((t) => t.id === id) ?? null;
  });

  constructor() {
    if (this.overrideTenantId) {
      this.stripOverrideFromUrl();
    }
  }

  /**
   * Called once per session after OperatorMe populates the tenant list. Reads
   * the saved tenant id from storage before releasing the tenant-resolution
   * gate so tenant-scoped GraphQL sees the correct Tenant-ID header.
   */
  setTenants(tenants: Tenant[]): Promise<void> {
    this._tenants.set(tenants);
    if (this.bootstrap) {
      return this.bootstrap;
    }
    this.bootstrap = this.runBootstrap(tenants);
    return this.bootstrap;
  }

  private async runBootstrap(tenants: Tenant[]): Promise<void> {
    try {
      if (!tenants.length || !tenants[0]?.id) {
        return;
      }

      const overrideId = this.overrideTenantId;
      const rawSaved = overrideId ?? localStorage.getItem(TenantService.STORAGE_KEY);
      const savedId = typeof rawSaved === 'string' && rawSaved.length > 0 ? rawSaved : null;
      const fromStorage = savedId ? tenants.find((t) => t.id === savedId) : undefined;
      const chosenId = fromStorage?.id ?? tenants[0].id;
      this._activeTenantId.set(chosenId);
    } finally {
      markTenantResolved();
    }
  }

  reset(): void {
    this.bootstrap = undefined;
    this._tenants.set([]);
    this._activeTenantId.set(null);
    resetTenantResolved();
    try {
      localStorage.removeItem(TenantService.STORAGE_KEY);
    } catch (e) {
      console.warn('[logout] failed clearing active tenant id', e);
    }
    try {
      this.injector.get(Apollo).client.clearStore().catch((e) => {
        console.warn('[logout] Apollo clearStore failed', e);
      });
    } catch (e) {
      console.warn('[logout] Apollo not available for clearStore', e);
    }
  }

  /**
   * Switches workspace tenant. Persists the new id, then hard-reloads to a
   * safe route with `?at=` so every store re-bootstraps against the new tenant.
   * Callers must not navigate after awaiting this — the document reloads away.
   */
  async switchTenant(id: string): Promise<void> {
    if (!id || id === this._activeTenantId()) {
      return;
    }

    this._activeTenantId.set(id);

    try {
      localStorage.setItem(TenantService.STORAGE_KEY, id);
    } catch (e) {
      console.warn('[tenant] persist active id failed', e);
    }

    this.reloadToSafeRoute(id);
  }

  reloadToSafeRoute(tenantId?: string): void {
    const query = tenantId ? `?${OVERRIDE_PARAM}=${encodeURIComponent(tenantId)}` : '';
    window.location.replace(`/${HOME_PATH}${query}`);
  }

  private stripOverrideFromUrl(): void {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has(OVERRIDE_PARAM)) {
        return;
      }
      url.searchParams.delete(OVERRIDE_PARAM);
      window.history.replaceState(
        window.history.state,
        '',
        `${url.pathname}${url.search}${url.hash}`
      );
    } catch {
      // non-fatal
    }
  }

  addTenant(tenant: Tenant): void {
    this._tenants.update((list) => [...list, tenant]);
  }
}
